/** A small line chart in SVG: conversion rate over time, one line per variation. No library. */

const NS = 'http://www.w3.org/2000/svg';

function s<K extends keyof SVGElementTagNameMap>(
  tag: K,
  attrs: Record<string, string | number> = {},
  ...children: Array<Node | string>
): SVGElementTagNameMap[K] {
  const el = document.createElementNS(NS, tag);
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, String(v));
  for (const c of children) el.append(c);
  return el;
}

export interface Line {
  name: string;
  /** one value per day; null where there is nothing to show yet */
  values: Array<number | null>;
}

/** Distinguishable without colour: each line also has its own dash pattern. */
const STYLES = [
  { color: 'var(--chart-0, #57606a)', dash: '' },
  { color: 'var(--chart-1, #0969da)', dash: '' },
  { color: 'var(--chart-2, #bf8700)', dash: '6 3' },
  { color: 'var(--chart-3, #8250df)', dash: '2 3' },
  { color: 'var(--chart-4, #1a7f37)', dash: '8 3 2 3' },
];
export const lineStyle = (i: number) => STYLES[i % STYLES.length]!;

export interface ChartData {
  days: string[];
  lines: Line[];
  /** formats a y value for the axis and the text description */
  format: (v: number) => string;
}

const W = 640;
const H = 260;
const M = { l: 56, r: 12, t: 12, b: 28 };

/** Round a maximum up to a tidy axis end (1, 2, 2.5, 5 times a power of ten). */
export function niceMax(max: number): number {
  if (!(max > 0)) return 1;
  const p = Math.pow(10, Math.floor(Math.log10(max)));
  for (const m of [1, 2, 2.5, 5, 10]) if (m * p >= max) return m * p;
  return 10 * p;
}

export function lineChart(data: ChartData): SVGSVGElement {
  const { days, lines, format } = data;
  const all = lines.flatMap((l) => l.values).filter((v): v is number => v !== null);
  const top = niceMax(Math.max(0, ...all));
  const n = Math.max(1, days.length - 1);
  const x = (i: number) =>
    M.l + (days.length <= 1 ? (W - M.l - M.r) / 2 : ((W - M.l - M.r) * i) / n);
  const y = (v: number) => M.t + (H - M.t - M.b) * (1 - v / top);

  const last = lines.map((l) => {
    for (let i = l.values.length - 1; i >= 0; i--) if (l.values[i] !== null) return l.values[i]!;
    return null;
  });
  const summary =
    `Conversion rate by day, ${days[0] ?? ''} to ${days[days.length - 1] ?? ''}. ` +
    lines
      .map((l, i) => `${l.name}: ${last[i] === null ? 'no data' : format(last[i]!)}`)
      .join('; ') +
    '.';

  const svg = s('svg', {
    viewBox: `0 0 ${W} ${H}`,
    role: 'img',
    'aria-label': summary,
    class: 'chart',
    preserveAspectRatio: 'xMidYMid meet',
  });
  svg.append(s('title', {}, summary));

  for (let t = 0; t <= 4; t++) {
    const v = (top * t) / 4;
    svg.append(
      s('line', { x1: M.l, x2: W - M.r, y1: y(v), y2: y(v), class: 'grid' }),
      s('text', { x: M.l - 6, y: y(v) + 4, class: 'tick', 'text-anchor': 'end' }, format(v)),
    );
  }
  if (days.length) {
    svg.append(
      s('text', { x: M.l, y: H - 8, class: 'tick', 'text-anchor': 'start' }, days[0]!),
      s(
        'text',
        { x: W - M.r, y: H - 8, class: 'tick', 'text-anchor': 'end' },
        days[days.length - 1]!,
      ),
    );
  }
  lines.forEach((l, k) => {
    const st = lineStyle(k);
    let d = '';
    let pen = false;
    l.values.forEach((v, i) => {
      if (v === null) {
        pen = false;
        return;
      }
      d += `${pen ? 'L' : 'M'}${x(i).toFixed(1)} ${y(v).toFixed(1)} `;
      pen = true;
    });
    if (d)
      svg.append(
        s('path', {
          d: d.trim(),
          fill: 'none',
          stroke: st.color,
          'stroke-width': 2.5,
          'stroke-linejoin': 'round',
          ...(st.dash ? { 'stroke-dasharray': st.dash } : {}),
        }),
      );
    // a single day is a dot, not a line
    const only = l.values.filter((v) => v !== null);
    if (only.length === 1) {
      const i = l.values.findIndex((v) => v !== null);
      svg.append(s('circle', { cx: x(i), cy: y(l.values[i]!), r: 4, fill: st.color }));
    }
  });
  return svg;
}
